/**
 * DocumentsTab - Document view with validation for Vehicle/Driver detail modals
 *
 * Uses DocumentValidationModal for documents requiring validation (license, ID, etc.)
 * and DocumentViewerModal for view-only display.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentAPI } from '../../../api.js';
import useUnifiedDocuments from '../useUnifiedDocuments.js';
import UnifiedDocumentsTab from '../UnifiedDocumentsTab.jsx';
import DocumentViewerModal from '../DocumentViewerModal.jsx';
import DocumentValidationModal from '../../documents/DocumentValidationModal.jsx';

// Document configuration by entity type.
// `title`/`labels` are NOT stored here (i18n piege #1: a module constant is evaluated
// once, before any language switch) — resolved at render via t(), see getDocumentConfigLabels().
const DOCUMENT_CONFIG = {
  vehicle: {
    scope: 'VEHICLE',
    types: [
      'VEHICLE_REGISTRATION',
      'VEHICLE_INSURANCE_CARD',
      'TECHNICAL_INSPECTION',
      'LEASE_CONTRACT',
      'PURCHASE_INVOICE',
      'MAINTENANCE_INVOICE',
      'VEHICLE_PHOTO',
      'VEHICLE_MISC'
    ],
    fetcher: documentAPI.getForVehicle
  },
  driver: {
    scope: 'DRIVER',
    types: [
      'DRIVING_LICENCE',
      'IDENTITY_DOCUMENT',
      'DRIVER_CERTIFICATION',
      'MEDICAL_CERTIFICATE',
      'DRIVER_SANCTION',
      'DRIVER_PHOTO'
    ],
    fetcher: documentAPI.getForDriver
  }
};

// Title + labels resolved at render. Labels reuse `documents.types.*` where the wording is
// identical; the ones that differ (e.g. "Carte verte assurance" vs "Carte verte") get their
// own key under `common.tabs.documentsTab.labels`.
function getDocumentConfigLabels(t, entityType) {
  if (entityType === 'driver') {
    return {
      title: t('common.tabs.documentsTab.driverTitle'),
      labels: [
        t('documents.types.DRIVING_LICENCE'),
        t('documents.types.IDENTITY_DOCUMENT'),
        t('common.tabs.documentsTab.labels.driverCertification'),
        t('documents.types.MEDICAL_CERTIFICATE'),
        t('common.tabs.documentsTab.labels.driverSanction'),
        t('documents.types.DRIVER_PHOTO')
      ]
    };
  }
  return {
    title: t('common.tabs.documentsTab.vehicleTitle'),
    labels: [
      t('documents.types.VEHICLE_REGISTRATION'),
      t('common.tabs.documentsTab.labels.vehicleInsuranceCard'),
      t('documents.types.TECHNICAL_INSPECTION'),
      t('common.tabs.documentsTab.labels.leaseContract'),
      t('common.tabs.documentsTab.labels.purchaseInvoice'),
      t('documents.types.MAINTENANCE_INVOICE'),
      t('documents.types.VEHICLE_PHOTO'),
      t('documents.types.VEHICLE_MISC')
    ]
  };
}

export function DocumentsTab({ entity, entityType, onMessage }) {
  const { t } = useTranslation();
  // Get document configuration
  const config = DOCUMENT_CONFIG[entityType] || DOCUMENT_CONFIG.vehicle;
  const { title, labels } = getDocumentConfigLabels(t, entityType);

  // Document modal states
  const [viewerDoc, setViewerDoc] = useState(null);
  const [validationDoc, setValidationDoc] = useState(null);

  // Unified document management via hook
  const {
    referenceId,
    documents,
    loading,
    reload
  } = useUnifiedDocuments({
    scope: config.scope,
    entityId: entity?.id,
    fetcher: config.fetcher
  });

  // Route to validation modal if pending, otherwise simple viewer
  const handleDocumentOpen = (doc) => {
    if (doc.validation_status === 'pending' || doc.validation_status === 'rejected') {
      setValidationDoc(doc);
    } else {
      setViewerDoc(doc);
    }
  };

  // After successful validation, reload documents and close modal
  const handleValidated = () => {
    reload();
    onMessage?.({
      title: t('common.tabs.documentsTab.validatedTitle'),
      message: t('common.tabs.documentsTab.validatedMessage'),
      type: 'success'
    });
  };

  return (
    <>
      <UnifiedDocumentsTab
        scope={config.scope}
        referenceId={referenceId}
        title={title}
        allowedTypes={config.types}
        acceptedTypeLabels={labels}
        documents={documents}
        loading={loading}
        onRefresh={reload}
        onUploadComplete={() => {}}
        onMessage={onMessage}
        onValidate={handleDocumentOpen}
        entityData={entity}
      />

      {/* Simple document viewer for already-validated docs */}
      <DocumentViewerModal
        isOpen={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
        doc={viewerDoc}
      />

      {/* Full validation modal for pending/rejected documents */}
      <DocumentValidationModal
        isOpen={!!validationDoc}
        onClose={() => setValidationDoc(null)}
        doc={validationDoc}
        entityData={entity}
        onValidated={handleValidated}
        onRejectClick={(doc) => {
          onMessage?.({
            title: t('common.tabs.documentsTab.rejectTitle'),
            message: t('common.tabs.documentsTab.rejectMessage'),
            type: 'info'
          });
        }}
        onCannotProvideClick={(doc) => {
          onMessage?.({
            title: t('common.tabs.documentsTab.exceptionTitle'),
            message: t('common.tabs.documentsTab.exceptionMessage'),
            type: 'info'
          });
        }}
      />
    </>
  );
}

export default DocumentsTab;
